import { ElectionType, electionTypeMap } from '../api/api-types';
import { useRouter } from '../application/Router';
import { createSearchParams, SearchParamsLike } from '../utils/search-params';

function useSafeSearchParams(): SearchParamsLike {
  const { searchParams, search } = useRouter();
  if (searchParams) return searchParams;
  if (typeof window !== 'undefined') {
    const s = search || window.location.search || '';
    return createSearchParams(s);
  }
  return createSearchParams(search || '');
}

// Valid values for the election query param
const lookupMap = {
  0: ElectionType.Folketingsvalg,
  dk: ElectionType.Folketingsvalg,
  1: ElectionType.EuropaParlamentsvalg,
  eu: ElectionType.EuropaParlamentsvalg,
  2: ElectionType.BothTypes,
  both: ElectionType.BothTypes,
};

/**
 * Get the current electionType from the url search params
 * */
export function useElectionType(): {
  electionType: ElectionType;
  electionTypeValue: string;
  isDefined: true;
};
// eslint-disable-next-line no-redeclare
export function useElectionType(): {
  electionType?: ElectionType;
  electionTypeValue?: string;
  isDefined: boolean;
} {
  const params = useSafeSearchParams();

  // Check for the election type in search params. Check for both `election` and `electionType`.
  const value = params.get('election') || params.get('electionType');
  // Check if the value exists, and return the election type that matches
  const key = (value && lookupMap[value.toLowerCase()]) ?? undefined;
  const electionType = key || ElectionType.Folketingsvalg;

  return {
    electionType,
    electionTypeValue: electionTypeMap[electionType],
    isDefined: key !== undefined,
  };
}

/**
 * Get the current party Id from the url search params
 * */
export function usePartyId() {
  const params = useSafeSearchParams();
  const partyId = params.get('party') || undefined;
  return { partyId };
}

export function useDeclarationType() {
  const params = useSafeSearchParams();
  const type = params.get('type');
  if (type === 'initiate' || type === 'confirm' || type === 'withdraw') {
    return { declarationType: type };
  }
  return { declarationType: undefined };
}

export function useChosenElectionType() {
  const params = useSafeSearchParams();
  const chosenElectionType = params.get('choice');
  if (chosenElectionType === 'dk' || chosenElectionType === 'eu') {
    return { chosenElectionType: chosenElectionType as 'dk' | 'eu' };
  }
  return { chosenElectionType: undefined };
}
